import { useState, type FormEvent } from 'react';
import {
  AlertTriangle,
  ArrowDownLeft,
  ArrowUpRight,
  Ban,
  Loader2,
  Scale,
  Search,
  ShieldCheck,
  Wallet,
} from 'lucide-react';

import {
  Badge,
  Button,
  Card,
  Drawer,
  EmptyState,
  ErrorAlert,
  Field,
  Input,
  PageHeader,
  Skeleton,
  StatCard,
  Table,
  Td,
  Textarea,
  Th,
} from '../components/ui';
import {
  DEPOSIT_STATUS_LABEL,
  TRANSACTION_LABEL,
  useDeposit,
  useDeposits,
  useDepositSummary,
  useRecordAdjustment,
  useRecordForfeit,
  useRecordPayment,
  useRecordRefund,
  useSetRequirement,
  type DepositStatus,
  type DepositTransactionType,
} from '../lib/deposits-api';
import { cn, errorMessage, fieldErrors, formatCurrency, formatDateTime } from '../lib/utils';

/**
 * Security deposit administration (spec §8, §36).
 *
 * The ledger is append-only: nothing on this screen edits a past entry. A
 * mistake is corrected by posting a compensating adjustment, which is why the
 * adjustment form demands a reason and the others do not.
 *
 * Holds and releases are absent by design — they belong to an order, and the
 * order pipeline writes them.
 */

type EntryKind = 'payment' | 'refund' | 'forfeit' | 'adjustment' | 'requirement';

const STATUS_TONE: Record<DepositStatus, 'success' | 'warning' | 'danger' | 'neutral' | 'brand'> = {
  NOT_REQUIRED: 'neutral',
  PENDING: 'warning',
  PARTIALLY_PAID: 'warning',
  PAID: 'success',
  HELD: 'brand',
  REFUNDED: 'neutral',
  FORFEITED: 'danger',
};

const TRANSACTION_TONE: Record<DepositTransactionType, string> = {
  PAYMENT: 'text-success-700',
  RELEASE: 'text-success-700',
  REFUND: 'text-ink',
  HOLD: 'text-brand-700',
  ADJUSTMENT: 'text-ink',
  FORFEIT: 'text-danger-700',
};

const STATUS_TABS: Array<{ label: string; value: DepositStatus[] | null; outstanding?: boolean }> = [
  { label: 'All', value: null },
  { label: 'Outstanding', value: null, outstanding: true },
  { label: 'Paid', value: ['PAID'] },
  { label: 'Held', value: ['HELD'] },
  { label: 'Refunded', value: ['REFUNDED'] },
  { label: 'Forfeited', value: ['FORFEITED'] },
];

const ENTRY_TITLE: Record<EntryKind, string> = {
  payment: 'Record a payment',
  refund: 'Record a refund',
  forfeit: 'Forfeit the deposit',
  adjustment: 'Post an adjustment',
  requirement: 'Change the requirement',
};

export function DepositsPage() {
  const [search, setSearch] = useState('');
  const [tab, setTab] = useState(0);
  const [page, setPage] = useState(1);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  // `STATUS_TABS[0]` is a literal in this file, so the fallback always resolves;
  // the non-null assertion states that rather than threading undefined onward.
  const activeTab = STATUS_TABS[tab] ?? STATUS_TABS[0]!;

  const deposits = useDeposits({
    page,
    limit: 20,
    search,
    status: activeTab.value ?? undefined,
    outstandingOnly: activeTab.outstanding,
  });
  const summary = useDepositSummary();

  return (
    <div>
      <PageHeader
        title="Security Deposits"
        description="Balances, the immutable ledger, and the requirement that gates bulk ordering."
      />

      {/* --- Counters ------------------------------------------------------- */}
      <div className="mb-5 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {summary.isLoading ? (
          [0, 1, 2, 3].map((index) => <Skeleton key={index} className="h-24" />)
        ) : (
          <>
            <StatCard
              label="Awaiting deposit"
              value={(summary.data?.pending ?? 0) + (summary.data?.partiallyPaid ?? 0)}
              icon={<AlertTriangle className="size-5" aria-hidden="true" />}
              tone="warning"
            />
            <StatCard
              label="Settled"
              value={summary.data?.paid ?? 0}
              icon={<ShieldCheck className="size-5" aria-hidden="true" />}
              tone="success"
            />
            <StatCard
              label="Held against orders"
              value={formatCurrency(summary.data?.totalHeld ?? 0)}
              icon={<Scale className="size-5" aria-hidden="true" />}
              tone="brand"
            />
            <StatCard
              label="Refundable balance"
              value={formatCurrency(summary.data?.totalBalance ?? 0)}
              icon={<Wallet className="size-5" aria-hidden="true" />}
              tone="brand"
            />
          </>
        )}
      </div>

      {/* --- Filters -------------------------------------------------------- */}
      <Card className="mb-4 p-4">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative min-w-56 flex-1">
            <Search
              className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-ink-subtle"
              aria-hidden="true"
            />
            <Input
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
                setPage(1);
              }}
              placeholder="Search company or customer code"
              className="pl-9"
              aria-label="Search deposits"
            />
          </div>

          <div className="flex flex-wrap gap-1.5" role="tablist" aria-label="Deposit status">
            {STATUS_TABS.map((entry, index) => (
              <button
                key={entry.label}
                type="button"
                role="tab"
                aria-selected={tab === index}
                onClick={() => {
                  setTab(index);
                  setPage(1);
                }}
                className={cn(
                  'rounded-lg px-3 py-1.5 text-sm font-medium transition-colors',
                  tab === index
                    ? 'bg-brand-600 text-white'
                    : 'text-ink-muted hover:bg-canvas hover:text-ink',
                )}
              >
                {entry.label}
              </button>
            ))}
          </div>
        </div>
      </Card>

      {/* --- Table ---------------------------------------------------------- */}
      <Card>
        {deposits.isLoading ? (
          <div className="space-y-2 p-4">
            {[0, 1, 2, 3, 4].map((index) => (
              <Skeleton key={index} className="h-12" />
            ))}
          </div>
        ) : deposits.isError ? (
          <div className="p-4">
            <ErrorAlert title="Could not load deposits">{errorMessage(deposits.error)}</ErrorAlert>
          </div>
        ) : (deposits.data?.items.length ?? 0) === 0 ? (
          <EmptyState
            title="No deposits match"
            description="Try a different status or clear the search."
          />
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>Customer</Th>
                <Th>Status</Th>
                <Th className="text-right">Required</Th>
                <Th className="text-right">Contributed</Th>
                <Th className="text-right">Held</Th>
                <Th className="text-right">Balance</Th>
                <Th>Gates</Th>
                <Th />
              </tr>
            </thead>
            <tbody>
              {deposits.data?.items.map((row) => (
                <tr key={row.customer.id} className="hover:bg-canvas/60">
                  <Td>
                    <p className="font-medium text-ink">{row.customer.companyName}</p>
                    <p className="text-xs text-ink-subtle">{row.customer.customerCode}</p>
                  </Td>
                  <Td>
                    <Badge tone={STATUS_TONE[row.deposit?.status ?? 'PENDING']}>
                      {DEPOSIT_STATUS_LABEL[row.deposit?.status ?? 'PENDING']}
                    </Badge>
                  </Td>
                  <Td className="text-right tabular-nums">
                    {formatCurrency(row.deposit?.requiredAmount ?? 0)}
                  </Td>
                  <Td className="text-right tabular-nums">
                    {formatCurrency(row.deposit?.contributedAmount ?? 0)}
                    {(row.deposit?.outstandingAmount ?? 0) > 0 && (
                      <span className="block text-xs text-warning-700">
                        {formatCurrency(row.deposit?.outstandingAmount ?? 0)} short
                      </span>
                    )}
                  </Td>
                  <Td className="text-right tabular-nums text-ink-muted">
                    {formatCurrency(row.deposit?.heldAmount ?? 0)}
                  </Td>
                  <Td className="text-right font-medium tabular-nums">
                    {formatCurrency(row.deposit?.balanceAmount ?? 0)}
                  </Td>
                  <Td>
                    <div className="flex gap-1">
                      <Badge tone={row.customer.isVerified ? 'success' : 'neutral'}>KYC</Badge>
                      <Badge tone={row.customer.canOrder ? 'success' : 'neutral'}>Order</Badge>
                    </div>
                  </Td>
                  <Td className="text-right">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setSelectedId(row.customer.id)}
                    >
                      Manage
                    </Button>
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}

        {deposits.data && deposits.data.pagination.totalPages > 1 && (
          <div className="flex items-center justify-between gap-3 border-t border-line px-4 py-3">
            <p className="text-sm text-ink-subtle">
              {deposits.data.pagination.total} deposits · page {deposits.data.pagination.page} of{' '}
              {deposits.data.pagination.totalPages}
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={!deposits.data.pagination.hasPreviousPage}
                onClick={() => setPage((current) => current - 1)}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={!deposits.data.pagination.hasNextPage}
                onClick={() => setPage((current) => current + 1)}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </Card>

      <DepositDrawer customerId={selectedId} onClose={() => setSelectedId(null)} />
    </div>
  );
}

/* ==========================================================================
   Detail drawer
   ========================================================================== */

function DepositDrawer({
  customerId,
  onClose,
}: {
  customerId: string | null;
  onClose: () => void;
}) {
  const detail = useDeposit(customerId);
  const [entry, setEntry] = useState<EntryKind | null>(null);

  const deposit = detail.data?.deposit;

  return (
    <Drawer
      open={Boolean(customerId)}
      onClose={() => {
        setEntry(null);
        onClose();
      }}
      title={detail.data?.customer.companyName ?? 'Security deposit'}
    >
      {detail.isLoading ? (
        <div className="space-y-3">
          <Skeleton className="h-24" />
          <Skeleton className="h-40" />
        </div>
      ) : detail.isError ? (
        <ErrorAlert title="Could not load this deposit">{errorMessage(detail.error)}</ErrorAlert>
      ) : (
        detail.data && (
          <div className="space-y-5">
            {/* --- Position ------------------------------------------------- */}
            <section>
              <div className="mb-3 flex flex-wrap items-center gap-2">
                <Badge tone={STATUS_TONE[deposit?.status ?? 'PENDING']}>
                  {DEPOSIT_STATUS_LABEL[deposit?.status ?? 'PENDING']}
                </Badge>
                <Badge tone={detail.data.customer.isVerified ? 'success' : 'neutral'}>
                  {detail.data.customer.isVerified ? 'KYC verified' : 'Not verified'}
                </Badge>
                <Badge tone={detail.data.customer.canOrder ? 'success' : 'neutral'}>
                  {detail.data.customer.canOrder ? 'Ordering on' : 'Ordering off'}
                </Badge>
              </div>

              <dl className="grid grid-cols-2 gap-3 rounded-lg border border-line p-4 text-sm">
                {[
                  ['Required', deposit?.requiredAmount ?? 0],
                  ['Contributed', deposit?.contributedAmount ?? 0],
                  ['Held against orders', deposit?.heldAmount ?? 0],
                  ['Refunded', deposit?.refundedAmount ?? 0],
                  ['Forfeited', deposit?.forfeitedAmount ?? 0],
                  ['Refundable balance', deposit?.balanceAmount ?? 0],
                ].map(([label, value]) => (
                  <div key={label as string}>
                    <dt className="text-xs text-ink-subtle">{label}</dt>
                    <dd className="font-medium tabular-nums text-ink">
                      {formatCurrency(value as number)}
                    </dd>
                  </div>
                ))}
              </dl>

              {(deposit?.outstandingAmount ?? 0) > 0 && (
                <p className="mt-2 rounded-lg bg-warning-50 px-3 py-2 text-sm text-warning-800">
                  {formatCurrency(deposit?.outstandingAmount ?? 0)} outstanding against a
                  requirement of {formatCurrency(deposit?.requiredAmount ?? 0)}.
                </p>
              )}
            </section>

            {/* --- Actions -------------------------------------------------- */}
            {entry ? (
              <EntryForm
                kind={entry}
                customerId={detail.data.customer.id}
                currentRequirement={deposit?.requiredAmount ?? detail.data.minimumAmount}
                availableBalance={deposit?.balanceAmount ?? 0}
                onDone={() => setEntry(null)}
              />
            ) : (
              <section>
                <h3 className="mb-2 text-sm font-semibold text-ink">Record a movement</h3>
                <div className="grid grid-cols-2 gap-2">
                  <Button onClick={() => setEntry('payment')}>
                    <ArrowDownLeft className="size-4" aria-hidden="true" />
                    Payment
                  </Button>
                  <Button variant="outline" onClick={() => setEntry('refund')}>
                    <ArrowUpRight className="size-4" aria-hidden="true" />
                    Refund
                  </Button>
                  <Button variant="outline" onClick={() => setEntry('adjustment')}>
                    <Scale className="size-4" aria-hidden="true" />
                    Adjustment
                  </Button>
                  <Button variant="danger" onClick={() => setEntry('forfeit')}>
                    <Ban className="size-4" aria-hidden="true" />
                    Forfeit
                  </Button>
                  <Button
                    variant="outline"
                    className="col-span-2"
                    onClick={() => setEntry('requirement')}
                  >
                    Change requirement
                  </Button>
                </div>
                <p className="mt-2 text-xs text-ink-subtle">
                  Holds and releases are written by the order pipeline, not from here.
                </p>
              </section>
            )}

            {/* --- Ledger --------------------------------------------------- */}
            <section>
              <h3 className="mb-2 text-sm font-semibold text-ink">Ledger</h3>
              <p className="mb-3 text-xs text-ink-subtle">
                Append-only. Corrections are posted as new rows, never edits.
              </p>

              {detail.data.transactions.length === 0 ? (
                <p className="rounded-lg border border-dashed border-line px-4 py-6 text-center text-sm text-ink-subtle">
                  Nothing recorded yet.
                </p>
              ) : (
                <ol className="space-y-2">
                  {detail.data.transactions.map((row) => (
                    <li key={row.id} className="rounded-lg border border-line p-3 text-sm">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="font-medium text-ink">{TRANSACTION_LABEL[row.type]}</p>
                          {row.reference && (
                            <p className="truncate text-xs text-ink-subtle">Ref: {row.reference}</p>
                          )}
                          {row.notes && <p className="mt-1 text-xs text-ink-muted">{row.notes}</p>}
                        </div>
                        <div className="shrink-0 text-right">
                          <p className={cn('font-medium tabular-nums', TRANSACTION_TONE[row.type])}>
                            {row.amount >= 0 ? '+' : '−'}
                            {formatCurrency(Math.abs(row.amount))}
                          </p>
                          <p className="text-xs text-ink-subtle tabular-nums">
                            → {formatCurrency(row.balanceAfter)}
                          </p>
                        </div>
                      </div>
                      <p className="mt-2 text-xs text-ink-subtle">
                        {formatDateTime(row.createdAt)}
                        {row.performedBy && ` · ${row.performedBy.name}`}
                      </p>
                    </li>
                  ))}
                </ol>
              )}
            </section>
          </div>
        )
      )}
    </Drawer>
  );
}

/* ==========================================================================
   One entry form, shared by every movement type
   ========================================================================== */

function EntryForm({
  kind,
  customerId,
  currentRequirement,
  availableBalance,
  onDone,
}: {
  kind: EntryKind;
  customerId: string;
  currentRequirement: number;
  availableBalance: number;
  onDone: () => void;
}) {
  const payment = useRecordPayment();
  const refund = useRecordRefund();
  const forfeit = useRecordForfeit();
  const adjustment = useRecordAdjustment();
  const requirement = useSetRequirement();

  const mutation =
    kind === 'payment'
      ? payment
      : kind === 'refund'
        ? refund
        : kind === 'forfeit'
          ? forfeit
          : kind === 'adjustment'
            ? adjustment
            : requirement;

  const [amount, setAmount] = useState(
    kind === 'requirement' ? String(currentRequirement) : '',
  );
  const [reference, setReference] = useState('');
  const [notes, setNotes] = useState('');

  const errors = fieldErrors(mutation.error);

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    const value = Number(amount);

    const onSuccess = () => onDone();

    if (kind === 'requirement') {
      requirement.mutate({ customerId, requiredAmount: value, reason: notes || undefined }, { onSuccess });
      return;
    }

    if (kind === 'adjustment') {
      adjustment.mutate(
        { customerId, amount: value, notes, reference: reference || undefined },
        { onSuccess },
      );
      return;
    }

    const body = {
      customerId,
      amount: value,
      reference: reference || undefined,
      notes: notes || undefined,
    };

    if (kind === 'payment') payment.mutate(body, { onSuccess });
    if (kind === 'refund') refund.mutate(body, { onSuccess });
    if (kind === 'forfeit') forfeit.mutate(body, { onSuccess });
  };

  return (
    <section className="rounded-lg border border-line p-4">
      <h3 className="mb-3 text-sm font-semibold text-ink">{ENTRY_TITLE[kind]}</h3>

      <form className="space-y-3" onSubmit={handleSubmit}>
        {mutation.isError && (
          <ErrorAlert title="Could not record this">{errorMessage(mutation.error)}</ErrorAlert>
        )}

        {(kind === 'refund' || kind === 'forfeit') && (
          <p className="rounded-lg bg-canvas px-3 py-2 text-xs text-ink-muted">
            {formatCurrency(availableBalance)} is available. Money held against open orders cannot
            be {kind === 'refund' ? 'refunded' : 'forfeited'}.
          </p>
        )}

        <Field
          label={kind === 'requirement' ? 'Required amount (₹)' : 'Amount (₹)'}
          htmlFor="amount"
          required
          error={errors['amount'] ?? errors['requiredAmount']}
          hint={
            kind === 'adjustment'
              ? 'Signed — negative corrects a receipt down.'
              : kind === 'requirement'
                ? 'Zero waives the deposit for this customer.'
                : undefined
          }
        >
          <Input
            id="amount"
            type="number"
            step="0.01"
            required
            value={amount}
            onChange={(event) => setAmount(event.target.value)}
          />
        </Field>

        {kind !== 'requirement' && (
          <Field label="Reference" htmlFor="reference" error={errors['reference']}>
            <Input
              id="reference"
              value={reference}
              onChange={(event) => setReference(event.target.value)}
              placeholder="UTR, cheque or transaction id"
            />
          </Field>
        )}

        <Field
          label={kind === 'requirement' ? 'Reason' : 'Notes'}
          htmlFor="notes"
          // A correction to a financial record without a stated reason is not
          // auditable, so the adjustment form demands one.
          required={kind === 'adjustment'}
          error={errors['notes'] ?? errors['reason']}
        >
          <Textarea
            id="notes"
            rows={2}
            required={kind === 'adjustment'}
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
            placeholder={
              kind === 'adjustment' ? 'Why is this correction being made?' : 'Optional'
            }
          />
        </Field>

        <div className="flex gap-2 pt-1">
          <Button type="submit" disabled={mutation.isPending} variant={kind === 'forfeit' ? 'danger' : 'primary'}>
            {mutation.isPending && <Loader2 className="size-4 animate-spin" aria-hidden="true" />}
            Record
          </Button>
          <Button type="button" variant="ghost" onClick={onDone}>
            Cancel
          </Button>
        </div>
      </form>
    </section>
  );
}
